import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { TextField } from '../text-field/text-field';
import { TextFieldAction } from '../text-field/text-field.types';

/**
 * Styled autocomplete / combobox that reuses `andor-text-field` for the input
 * (so it inherits the app's field styling, label, validation and ticker) and
 * renders a suggestion panel in a CDK overlay that auto-flips above/below
 * (below preferred).
 *
 * Data is parent-driven: the component emits `(queryChange)` with the current
 * input text and the parent supplies the already-filtered `items` (local
 * filtering OR server fetch-on-type). Each row is rendered via a projected
 * `<ng-template #option let-item>`. Also a `ControlValueAccessor`, so the
 * selected item can be bound with `[formControl]`/`[(ngModel)]`; `(selected)`
 * is emitted alongside.
 */
@Component({
  selector: 'andor-autocomplete-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, OverlayModule, TextField],
  templateUrl: './autocomplete-field.html',
  styleUrl: './autocomplete-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteField),
      multi: true,
    },
  ],
})
export class AutocompleteField implements ControlValueAccessor {
  readonly label = input('');
  readonly placeholder = input('');
  readonly labelTooltip = input('');
  // Already-filtered suggestions supplied by the parent. `any`-typed (like
  // Material's MatOption value) so call sites avoid generic-variance friction.
  readonly items = input<any[]>([]);
  // How a selected item renders as the input's text value.
  readonly displayWith = input<(item: any) => string>((item) => (item == null ? '' : String(item)));
  // Stable identity for @for tracking and selection.
  readonly itemKey = input<(item: any) => string>((item) => String(item));
  // Optional message shown when there are no items (e.g. "No rooms available").
  readonly emptyText = input('');
  // Inline action buttons rendered inside the input itself (forwarded to the
  // underlying text-field) — e.g. a refresh button on the right.
  readonly inlineActions = input<TextFieldAction[]>([]);
  readonly inlineActionsPosition = input<'start' | 'end'>('end');
  // Open the suggestion panel automatically while the field is still empty, so
  // the available options are visible without a click/focus (e.g. the room
  // picker). Fires once, the first time a non-empty list arrives.
  readonly openByDefault = input(false);

  readonly queryChange = output<string>();
  readonly selected = output<any>();

  protected readonly searchControl = new FormControl('', { nonNullable: true });

  protected readonly open = signal(false);
  protected readonly activeIndex = signal(-1);

  // Projected `<ng-template #option let-item let-active="active">`.
  protected readonly optionTpl = contentChild<TemplateRef<unknown>>('option');
  protected readonly originRef = viewChild<ElementRef<HTMLElement>>('originEl');

  private readonly _host = inject(ElementRef<HTMLElement>);

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
  // Guards the one-shot `openByDefault` auto-open below.
  private _autoOpened = false;

  // Below preferred; flip above when there's no room beneath.
  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  constructor() {
    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      // Typing invalidates a prior selection.
      this._onChange(null);
      this.activeIndex.set(-1);
      this.open.set(true);
      this.queryChange.emit(value ?? '');
    });

    // One-shot auto-open: when `openByDefault` is set, surface the panel the
    // first time the parent supplies a non-empty list — but only while the
    // field is still empty (a prefilled selection is left closed). After this,
    // normal behaviour applies: typing/clearing re-opens it, Escape/blur close
    // it, and this never fights a manual dismiss.
    effect(() => {
      if (!this.openByDefault() || this._autoOpened) return;
      if (this.items().length === 0) return;
      this._autoOpened = true;
      if (this.searchControl.value === '') this.open.set(true);
    });
  }

  protected readonly showPanel = () =>
    this.open() && (this.items().length > 0 || this.emptyText().length > 0);

  protected get overlayWidth(): number | null {
    return this.originRef()?.nativeElement?.offsetWidth ?? null;
  }

  // --- ControlValueAccessor ---
  writeValue(item: any): void {
    this.searchControl.setValue(item != null ? this.displayWith()(item) : '', {
      emitEvent: false,
    });
  }
  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.searchControl.disable({ emitEvent: false });
    else this.searchControl.enable({ emitEvent: false });
  }

  protected onFocusIn(): void {
    if (this.items().length > 0 || this.emptyText().length > 0) this.open.set(true);
  }

  protected onFocusOut(event: FocusEvent): void {
    // Stay open while focus remains within the field. The overlay panel lives
    // outside the host, but option rows preventDefault on mousedown so a click
    // never blurs the input — so a real focus-out here means the user left.
    const next = event.relatedTarget as Node | null;
    if (next && this._host.nativeElement.contains(next)) return;
    this.open.set(false);
    this._onTouched();
  }

  protected onKeydown(event: KeyboardEvent): void {
    const items = this.items();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open()) this.open.set(true);
        this.activeIndex.set(Math.min(this.activeIndex() + 1, items.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.set(Math.max(this.activeIndex() - 1, 0));
        break;
      case 'Enter': {
        const idx = this.activeIndex();
        if (this.open() && idx >= 0 && idx < items.length) {
          event.preventDefault();
          this.select(items[idx]);
        }
        break;
      }
      case 'Escape':
        this.open.set(false);
        break;
    }
  }

  protected select(item: any): void {
    this.searchControl.setValue(this.displayWith()(item), { emitEvent: false });
    this._onChange(item);
    this.selected.emit(item);
    this.open.set(false);
  }

  protected trackItem = (_: number, item: any): string => this.itemKey()(item);
}
